import { Component, effect, inject, signal, OnInit } from '@angular/core';

import {
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import { ModalService } from '../../../../core/services/modal.service';
import { Load } from '../../../../core/models/load.model';
import { ButtonComponent } from '../../../../shared/ui/button/button.component';
import { FormCardComponent } from '../../../../shared/ui/forms/form-card/form-card.component';
import { FormFieldComponent } from '../../../../shared/ui/forms/form-field/form-field.component';
import {
  SelectComponent,
  SelectOption,
} from '../../../../shared/ui/forms/select/select.component';
import { AppType, KeyValue } from '../../../../core/models/app-types.model';
import {
  ChipOption,
  SelectChipComponent,
} from '../../../../shared/ui/forms/select-chip/select-chip.component';
import { KeyValueArrayInputComponent } from '../../../../shared/ui/forms/key-value-array-input/key-value-array-input.component';
import { NumberInputComponent } from '../../../../shared/ui/forms/number-input/number-input.component';
import { InputComponent } from '../../../../shared/ui/forms/input/input.component';
import { CheckboxComponent } from '../../../../shared/ui/forms/checkbox/checkbox.component';
import { EquipmentOptions } from '../../../../core/enums/equipment.enum';
import { ReferenceOptions } from '../../../../core/enums/reference.enum';
import { ItemOptions } from '../../../../core/enums/item.enum';
import { EndorsementOptions } from '../../../../core/enums/endorsements.enum';
import { TrackingAppOptions } from '../../../../core/enums/tracking-app.enum';
import { LoadService } from '../../../../core/services/load.service';
import { ToastService } from '../../../../shared/ui/toast/toast.service';
import { LoadingService } from '../../../../shared/ui/loading/loading.service';

@Component({
  selector: 'app-edit-details-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    ButtonComponent,
    FormCardComponent,
    FormFieldComponent,
    SelectComponent,
    KeyValueArrayInputComponent,
    SelectChipComponent,
    NumberInputComponent,
    InputComponent,
    CheckboxComponent
],
  templateUrl: './edit-details-form.component.html',
})
export class EditDetailsFormComponent implements OnInit {
  private readonly modalService = inject(ModalService);
  private readonly loadService = inject(LoadService);
  private readonly toastService = inject(ToastService);
  private readonly loadingService = inject(LoadingService);
  private readonly fb = inject(FormBuilder);

  // Signal to track equipment type for conditional UI
  isReeferLoad = signal(false);
  isTracked = signal(false);

  equipmentOptions: SelectOption[] = EquipmentOptions.map((t) => ({
    value: t.value,
    label: t.label,
  }));
  referenceTypes: AppType[] = ReferenceOptions.map((t) => ({
    id: t.value,
    label: t.label,
  }));
  itemTypes: AppType[] = ItemOptions.map((t) => ({
    id: t.value,
    label: t.label,
  }));
  endorsementOptions: ChipOption[] = [...EndorsementOptions];
  trackingAppOptions: SelectOption[] = TrackingAppOptions.map((t) => ({
    value: t.value,
    label: t.label,
  }));

  protected readonly load: Load = this.modalService.state()!.data.load;
  protected readonly onSuccess = this.modalService.state()!.data.onSuccess;
  protected readonly isSaving = signal(false);
  protected form: FormGroup;

  constructor() {
    this.form = this.fb.group({
      commodity: [null],
      trailerType: [null],
      weight: [null],
      temperature: [null],
      isContinuous: [false],
      isTracked: [false],
      trackingApp: [null],
      endorsements: [[]],
      referenceNumbers: [[]],
      itemCounts: [[]],
    });

    // Sync form controls to signals
    this.form
      .get('trailerType')!
      .valueChanges.subscribe((value) =>
        this.isReeferLoad.set(value === 'REEFER')
      );
    this.form
      .get('isTracked')!
      .valueChanges.subscribe((value) => this.isTracked.set(value));

    // Initialize form with data from the load
    this.initializeForm();

    // Effect to manage enabling/disabling form groups
    effect(() => this.updateFormState());
  }

  private initializeForm(): void {
    this.form.patchValue(
      {
        commodity: this.load.commodity || null,
        trailerType: this.load.trailerType || null,
        weight: this.load.weightLbs || null,
        temperature: this.load.temperature?.value || null,
        isContinuous: this.load.temperature?.isContinuous || false,
        isTracked: this.load.isTracked || false,
        trackingApp: this.load.trackingApp || null,
        endorsements: this.load.endorsements || [],
        referenceNumbers: this.load.referenceNumbers || [],
        itemCounts: this.load.itemCounts || [],
        // ... patch other values from this.load
      },
      { emitEvent: false } // Prevent infinite loops
    );
    this.isReeferLoad.set(this.load.trailerType === 'REEFER');
    this.isTracked.set(this.load.isTracked || false);
  }

  private updateFormState(): void {
    const isReeferLoad = this.isReeferLoad();
    const temperature = this.form.get('temperature');
    const isContinuous = this.form.get('isContinuous');
    const isTracked = this.isTracked();
    const trackingApp = this.form.get('trackingApp');
    if (isReeferLoad) {
      temperature?.enable({ emitEvent: false });
      isContinuous?.enable({ emitEvent: false });
    } else {
      temperature?.disable({ emitEvent: false });
      isContinuous?.disable({ emitEvent: false });
    }
    if (isTracked) {
      trackingApp?.enable({ emitEvent: false });
    } else {
      trackingApp?.disable({ emitEvent: false });
    }
  }

  ngOnInit(): void {
    // Ensure at least one row exists for a new form
    if (this.referenceNumbers.length === 0) {
      this.addReferenceNumber();
    }
    if (this.itemCounts.length === 0) {
      this.addItemCount();
    }
  }

  // --- FormArray Getters ---
  get referenceNumbers(): FormArray {
    return this.form.get('referenceNumbers') as FormArray;
  }

  get itemCounts(): FormArray {
    return this.form.get('itemCounts') as FormArray;
  }

  // --- Reference Number Methods ---
  addReferenceNumber(value: KeyValue | null = null): void {
    this.referenceNumbers.push(this.fb.control(value));
  }

  removeReferenceNumber(index: number): void {
    this.referenceNumbers.removeAt(index);
  }

  // --- Item Count Methods ---
  addItemCount(value: KeyValue | null = null): void {
    this.itemCounts.push(this.fb.control(value));
  }

  removeItemCount(index: number): void {
    this.itemCounts.removeAt(index);
  }

  cancel(): void {
    this.modalService.close();
  }

  async save(): Promise<void> {
    if (this.form.invalid) {
      // Optionally mark all fields as touched to show errors
      this.form.markAllAsTouched();
      return;
    }

    // Set local saving state
    this.isSaving.set(true);

    // Show global loading overlay
    this.loadingService.show({
      variant: 'overlay',
      title: 'Updating Stop Details',
      subtitle: 'Saving your changes...',
    });

    // Map form value to a partial Load object for updating
    const formValue = this.form.getRawValue();
    const updatedData: Partial<Load> = {
      // ... map formValue fields to Load model fields
      commodity: formValue.commodity,
      trailerType: formValue.trailerType,
      weightLbs: formValue.weight,
      temperature: {
        value: formValue.temperature,
        isContinuous: formValue.isContinuous,
      },
      isTracked: formValue.isTracked,
      trackingApp: formValue.trackingApp,
      endorsements: formValue.endorsements,
      referenceNumbers: formValue.referenceNumbers,
      itemCounts: formValue.itemCounts,
    };

    this.loadService.updateLoad(this.load.id, updatedData).subscribe({
      next: () => {
        this.loadingService.hide();
        this.modalService.close();
        this.onSuccess?.();
        setTimeout(() => {
          this.toastService.show('success', 'Load details updated successfully', {
            title: 'Success',
            duration: 4000,
          });
        }, 100);
      },
      error: (_error) => {
        this.loadingService.hide();
        this.toastService.show(
          'error',
          'Failed to update load details. Please try again.',
          {
            title: 'Update Failed',
            showClose: true,
            duration: 0,
          }
        );
      },
      complete: () => {
        this.isSaving.set(false);
      }
    });
  }
}
